/* eslint-disable max-lines, no-negated-condition, @typescript-eslint/explicit-module-boundary-types */
import {
	BufferGeometry,
	Line,
	LineBasicMaterial,
	Object3D,
	Scene,
	SpotLight,
	Vector3
} from 'three';

import { calculateHighlightLightSource, HighlightCircleConfig } from './arc-calculation';
import {
	createDebugLine,
	createDebugSphere,
	createEmptyDebugObjects,
	DebugColors,
	DebugSizes,
	disposeDebugObjects,
	HighlightDebugObjects,
	setDebugObjectsVisibility,
	updateDebugLine
} from './debug-objects';
import { getLabeledFaces, getObjectCenter, isTopOnlyObject, LabeledFaces } from './face-detection';

/**
 * Configuration for creating a highlight
 */
export interface HighlightConfig {
	/** Scene to add objects to */
	scene: Scene;
	/** Circle configuration for light positioning */
	circle: HighlightCircleConfig;
	/** Whether debug mode is enabled */
	debugMode?: boolean;
	/** Spotlight configuration */
	spotlight?: {
		color?: number;
		intensity?: number;
		angle?: number;
		penumbra?: number;
		decay?: number;
		distance?: number;
	};
	/** Existing spotlight to reuse (for switching highlights) */
	reuseSpotlight?: SpotLight | null;
}

/**
 * Manages a single highlight for a Three.js object
 *
 * Handles:
 * - Spotlight creation and positioning
 * - Target and source position calculation
 * - Debug visualization objects
 * - Face detection for highlight targeting
 */
export class Highlight {
	/** The highlighted object */
	readonly targetObject: Object3D;

	/** Object ID for identification */
	readonly objectId: string;

	/** Scene reference */
	#scene: Scene;

	/** Circle configuration */
	#circle: HighlightCircleConfig;

	/** The spotlight for this highlight */
	#spotLight: SpotLight | null = null;

	/** Debug visualization objects */
	#debugObjects: HighlightDebugObjects;

	/** Whether this is a top-only object (countertop, etc.) */
	#isTopOnly: boolean;

	/** Labeled faces of the object */
	#faces: LabeledFaces;

	/** Calculated positions */
	#highlightTarget: Vector3;
	#highlightLightSource: Vector3;
	#objectCenter: Vector3;

	/** Debug mode state */
	#debugMode: boolean;

	constructor (targetObject: Object3D, objectId: string, config: HighlightConfig) {
		this.targetObject = targetObject;
		this.objectId = objectId;
		this.#scene = config.scene;
		this.#circle = config.circle;
		this.#debugMode = config.debugMode ?? false;

		const isReusingSpotlight = config.reuseSpotlight != null;
		// console.log(`Highlight: Creating for ${objectId}`, {
			// debugMode: this.#debugMode,
			// hasScene: this.#scene !== null,
			// hasCircle: this.#circle !== null,
			// reusingSpotlight: isReusingSpotlight
		// });

		// Initialize debug objects container
		this.#debugObjects = createEmptyDebugObjects();

		// Calculate object properties
		this.#objectCenter = getObjectCenter(targetObject);
		this.#faces = getLabeledFaces(targetObject);
		this.#isTopOnly = isTopOnlyObject(targetObject);

		// Calculate highlight positions
		this.#highlightTarget = this.calculateHighlightTarget();
		this.#highlightLightSource = this.calculateHighlightLightSource();

		// console.log(`Highlight: Positions calculated for ${objectId}`, {
			// target: { x: this.#highlightTarget.x.toFixed(1), y: this.#highlightTarget.y.toFixed(1), z: this.#highlightTarget.z.toFixed(1) },
			// source: { x: this.#highlightLightSource.x.toFixed(1), y: this.#highlightLightSource.y.toFixed(1), z: this.#highlightLightSource.z.toFixed(1) }
		// });

		// Create or reuse spotlight
		if (config.reuseSpotlight) {
			// Reuse existing spotlight from previous highlight
			this.#spotLight = config.reuseSpotlight;
			this.#spotLight.name = `highlight-spotlight-${this.objectId}`;
			// Update target to point at the new object's face
			const targetFacePoint = this.#isTopOnly
				? this.#faces.top.point
				: this.#faces.front.point;
			this.#spotLight.target.position.copy(targetFacePoint);
			this.#spotLight.target.updateMatrixWorld();
			// console.log(`Highlight: Reused existing spotlight for ${objectId}`);
		} else {
			// Create new spotlight
			this.createSpotlight(config.spotlight);
		}

		// Create debug objects if debug mode is enabled
		if (this.#debugMode) {
			// console.log(`Highlight: Creating debug objects for ${objectId}`);
			this.createDebugObjects();
			// Ensure all debug objects are visible
			setDebugObjectsVisibility(this.#debugObjects, true);
		}
	}

	/** Get the highlight target position */
	get highlightTarget (): Vector3 {
		return this.#highlightTarget.clone();
	}

	/** Get the highlight light source position */
	get highlightLightSource (): Vector3 {
		return this.#highlightLightSource.clone();
	}

	/** Get the object center */
	get objectCenter (): Vector3 {
		return this.#objectCenter.clone();
	}

	/** Get whether this is a top-only highlight */
	get isTopOnlyHighlight (): boolean {
		return this.#isTopOnly;
	}

	/** Get the spotlight */
	get light (): SpotLight | null {
		return this.#spotLight;
	}

	/** Get the front face */
	get frontFace () {
		return this.#faces.front;
	}

	/** Get the top face */
	get topFace () {
		return this.#faces.top;
	}

	/** Get the face point where the spotlight should aim */
	get spotlightAimPoint (): Vector3 {
		return this.#isTopOnly
			? this.#faces.top.point.clone()
			: this.#faces.front.point.clone();
	}

	/** Get debug mode state */
	get debugMode (): boolean {
		return this.#debugMode;
	}

	/**
	 * Calculate the highlight target position based on object type
	 */
	private calculateHighlightTarget (): Vector3 {
		const target = new Vector3();

		if (this.#isTopOnly) {
			// For top face: position on perpendicular line at highlight circle height
			target.set(
				this.#faces.top.point.x,
				this.#circle.y,
				this.#faces.top.point.z
			);
		} else {
			// For front face: position 30" away from front face center along perpendicular
			// (was 18", increased by 12" for better visibility)
			target.copy(this.#faces.front.point).add(this.#faces.front.normal.clone().multiplyScalar(30));
		}

		return target;
	}

	/**
	 * Calculate the highlight light source position on the circle
	 */
	private calculateHighlightLightSource (): Vector3 {
		return calculateHighlightLightSource(
			this.#faces.front.point,
			this.#faces.front.normal,
			this.#circle
		);
	}

	/**
	 * Create the spotlight for this highlight
	 */
	private createSpotlight (config?: HighlightConfig['spotlight']): void {
		// Match legacy spotlight configuration: gold color (0xffd700), intensity 1000
		this.#spotLight = new SpotLight(
			config?.color ?? 0xffd700, // Gold/warm white color (matches legacy)
			config?.intensity ?? 0, // Start at 0, animate up
			config?.distance ?? 100, // Match legacy distance
			config?.angle ?? Math.PI / 4, // 45 degrees (matches legacy)
			config?.penumbra ?? 0.4, // Match legacy penumbra
			config?.decay ?? 1.5 // Match legacy decay
		);

		this.#spotLight.name = `highlight-spotlight-${this.objectId}`;
		this.#spotLight.castShadow = false; // Match legacy: no shadows for UI highlighting
		this.#spotLight.visible = true; // Ensure light is visible

		// Position at the light source
		this.#spotLight.position.copy(this.#highlightLightSource);

		// Point at the target face (front or top)
		const targetFacePoint = this.#isTopOnly
			? this.#faces.top.point
			: this.#faces.front.point;
		this.#spotLight.target.position.copy(targetFacePoint);

		// Add to scene
		this.#scene.add(this.#spotLight);
		this.#scene.add(this.#spotLight.target);

		// console.log(`Highlight.createSpotlight: Created spotlight for ${this.objectId}`, {
			// name: this.#spotLight.name,
			// visible: this.#spotLight.visible,
			// intensity: this.#spotLight.intensity,
			// position: { x: this.#spotLight.position.x.toFixed(1), y: this.#spotLight.position.y.toFixed(1), z: this.#spotLight.position.z.toFixed(1) },
			// targetPosition: { x: this.#spotLight.target.position.x.toFixed(1), y: this.#spotLight.target.position.y.toFixed(1), z: this.#spotLight.target.position.z.toFixed(1) },
			// inScene: this.#scene.children.includes(this.#spotLight),
			// targetInScene: this.#scene.children.includes(this.#spotLight.target)
		// });
	}

	/**
	 * Create debug visualization objects
	 */
	private createDebugObjects (): void {
		// console.log(`Highlight.createDebugObjects: Starting for ${this.objectId}`);
		// Target sphere (light yellow)
		this.#debugObjects.targetSphere = createDebugSphere(
			this.#highlightTarget,
			DebugColors.LIGHT_YELLOW,
			DebugSizes.SPHERE_DIAMETER,
			`highlight-target-${this.objectId}`
		);
		this.#scene.add(this.#debugObjects.targetSphere);
		// console.log(`Highlight.createDebugObjects: Added target sphere for ${this.objectId}, visible: ${this.#debugObjects.targetSphere.visible}`);

		// Source sphere (yellow) - 2" sphere on the yellow circle showing where this highlight light starts
		// This sphere stays FIXED at the source position as a reference point
		this.#debugObjects.sourceSphere = createDebugSphere(
			this.#highlightLightSource,
			DebugColors.YELLOW,
			DebugSizes.SPHERE_DIAMETER,
			`highlight-source-${this.objectId}`
		);
		this.#scene.add(this.#debugObjects.sourceSphere);
		// console.log(`Highlight.createDebugObjects: Added source sphere (light start point) for ${this.objectId} at`, {
		// 	x: this.#highlightLightSource.x.toFixed(1),
		// 	y: this.#highlightLightSource.y.toFixed(1),
		// 	z: this.#highlightLightSource.z.toFixed(1)
		// });

		// Path line from source to target
		this.#debugObjects.pathLine = createDebugLine(
			this.#highlightLightSource,
			this.#highlightTarget,
			DebugColors.YELLOW,
			`highlight-path-${this.objectId}`
		);
		this.#scene.add(this.#debugObjects.pathLine);

		// Front face sphere and vector
		this.#debugObjects.frontFaceSphere = createDebugSphere(
			this.#faces.front.point,
			DebugColors.LIGHT_YELLOW,
			DebugSizes.SPHERE_DIAMETER,
			`front-face-${this.objectId}`
		);
		this.#scene.add(this.#debugObjects.frontFaceSphere);

		const frontVectorEnd = this.#isTopOnly
			? this.#faces.front.point.clone().add(this.#faces.front.normal.clone().multiplyScalar(DebugSizes.VECTOR_LENGTH))
			: this.#highlightTarget;

		this.#debugObjects.frontFaceVector = createDebugLine(
			this.#faces.front.point,
			frontVectorEnd,
			DebugColors.LIGHT_YELLOW,
			`front-face-vector-${this.objectId}`
		);
		this.#scene.add(this.#debugObjects.frontFaceVector);

		// Top face sphere and vector
		this.#debugObjects.topFaceSphere = createDebugSphere(
			this.#faces.top.point,
			DebugColors.LIGHT_YELLOW,
			DebugSizes.SPHERE_DIAMETER,
			`top-face-${this.objectId}`
		);
		this.#scene.add(this.#debugObjects.topFaceSphere);

		const topVectorEnd = this.#isTopOnly
			? this.#highlightTarget
			: this.#faces.top.point.clone().add(this.#faces.top.normal.clone().multiplyScalar(DebugSizes.VECTOR_LENGTH));

		this.#debugObjects.topFaceVector = createDebugLine(
			this.#faces.top.point,
			topVectorEnd,
			DebugColors.LIGHT_YELLOW,
			`top-face-vector-${this.objectId}`
		);
		this.#scene.add(this.#debugObjects.topFaceVector);

		// console.log(`Highlight.createDebugObjects: Completed for ${this.objectId}`, {
			// targetSphere: !!this.#debugObjects.targetSphere,
			// sourceSphere: !!this.#debugObjects.sourceSphere,
			// pathLine: !!this.#debugObjects.pathLine,
			// frontFaceSphere: !!this.#debugObjects.frontFaceSphere,
			// topFaceSphere: !!this.#debugObjects.topFaceSphere,
			// spotlight: !!this.#spotLight,
			// spotlightVisible: this.#spotLight?.visible
		// });
	}

	/**
	 * Update spotlight position
	 * @param position - New position for the spotlight
	 * @param targetPosition - Optional target position to look at (if not provided, uses face point)
	 */
	updateLightPosition (position: Vector3, targetPosition?: Vector3): void {
		if (this.#spotLight) {
			this.#spotLight.position.copy(position);

			// Update spotlight target
			if (targetPosition) {
				// Use provided target (for animation between objects)
				this.#spotLight.target.position.copy(targetPosition);
			} else {
				// Default: point at the face center (front or top)
				const targetFacePoint = this.#isTopOnly
					? this.#faces.top.point
					: this.#faces.front.point;
				this.#spotLight.target.position.copy(targetFacePoint);
			}
			this.#spotLight.target.updateMatrixWorld();

			// Update debug path line from current light position to target
			if (this.#debugObjects.pathLine && this.#debugMode) {
				updateDebugLine(this.#debugObjects.pathLine, position, this.#spotLight.target.position);
			}

			// NOTE: Source sphere stays FIXED on the yellow circle as a reference point
			// It does NOT follow the light - it shows where the light originated from
		}
	}

	/**
	 * Re-aim at the object where it is NOW. The faces, centre, target and source
	 * are computed once at construction, so a highlighted appliance that is
	 * being dragged would otherwise leave its spotlight behind. Call per frame
	 * while the object moves; the spotlight jumps (no tween) so it tracks.
	 */
	follow (): void {
		this.targetObject.updateWorldMatrix(true, true);
		this.#objectCenter = getObjectCenter(this.targetObject);
		this.#faces = getLabeledFaces(this.targetObject);
		this.#highlightTarget = this.calculateHighlightTarget();
		this.#highlightLightSource = this.calculateHighlightLightSource();
		// rest where the fly-in ends (in front of the face), not back at its source on the circle
		this.updateLightPosition(this.#highlightTarget);
	}

	/**
	 * Update spotlight intensity
	 */
	setIntensity (intensity: number): void {
		if (this.#spotLight) {
			this.#spotLight.intensity = intensity;
		}
	}

	/**
	 * Get current spotlight intensity
	 */
	getIntensity (): number {
		return this.#spotLight?.intensity ?? 0;
	}

	/**
	 * Set debug mode visibility
	 */
	setDebugMode (enabled: boolean): void {
		this.#debugMode = enabled;

		if (enabled && !this.#debugObjects.targetSphere) {
			// Create debug objects if they don't exist
			this.createDebugObjects();
		} else if (!enabled && this.#debugObjects.targetSphere) {
			// Hide debug objects
			setDebugObjectsVisibility(this.#debugObjects, false);
		} else if (enabled && this.#debugObjects.targetSphere) {
			// Show debug objects
			setDebugObjectsVisibility(this.#debugObjects, true);
		}
	}

	/**
	 * Create/update transition arc for animation between highlights
	 * Shows the path the highlight light follows when switching between objects
	 */
	setTransitionArc (points: Vector3[]): void {
		if (!this.#debugMode) return;

		// console.log(`Highlight.setTransitionArc: Creating arc with ${points.length} points for ${this.objectId}`);

		if (!this.#debugObjects.transitionArc) {
			const geometry = new BufferGeometry().setFromPoints(points);
			// Use bright orange/red color and thicker line for visibility
			const material = new LineBasicMaterial({
				color: DebugColors.ORANGE,
				linewidth: 2 // Note: linewidth > 1 only works on some platforms
			});
			this.#debugObjects.transitionArc = new Line(geometry, material);
			this.#debugObjects.transitionArc.name = `transition-arc-${this.objectId}`;
			this.#scene.add(this.#debugObjects.transitionArc);
		} else {
			this.#debugObjects.transitionArc.geometry.dispose();
			this.#debugObjects.transitionArc.geometry = new BufferGeometry().setFromPoints(points);
		}
		this.#debugObjects.transitionArc.visible = true;
		// console.log(`Highlight.setTransitionArc: Arc visible=${this.#debugObjects.transitionArc.visible}`);
	}

	/**
	 * Hide transition arc
	 */
	hideTransitionArc (): void {
		if (this.#debugObjects.transitionArc) {
			this.#debugObjects.transitionArc.visible = false;
		}
	}

	/**
	 * Update path line endpoints
	 */
	updatePathLine (start: Vector3, end: Vector3): void {
		if (this.#debugObjects.pathLine) {
			updateDebugLine(this.#debugObjects.pathLine, start, end);
		}
	}

	/**
	 * Clean up and remove all objects from the scene
	 */
	dispose (): void {
		// Remove spotlight
		if (this.#spotLight) {
			this.#scene.remove(this.#spotLight);
			this.#scene.remove(this.#spotLight.target);
			this.#spotLight.dispose();
			this.#spotLight = null;
		}

		// Remove debug objects
		disposeDebugObjects(this.#debugObjects, this.#scene);
	}

	/**
	 * Clean up everything EXCEPT the spotlight (for reuse when switching highlights)
	 * The spotlight will be transferred to the new highlight
	 */
	disposeWithoutLight (): void {
		// Clear spotlight reference but don't remove it from scene or dispose it
		// The new Highlight will take ownership
		this.#spotLight = null;

		// Remove debug objects
		disposeDebugObjects(this.#debugObjects, this.#scene);
	}
}

